import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, Spinner } from "@/components/dashboard/DashBits";

function AdminPlans() {
  const { t } = useI18n();
  const [plans, setPlans] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/plans/${edit.key}`, {
        name: edit.name, max_profiles: Number(edit.max_profiles),
        price_monthly: Number(edit.price_monthly), price_yearly: Number(edit.price_yearly),
      });
      toast.success(t("dash.planUpdated"));
      setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (plans === null) return <Spinner />;
  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus-ring";

  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-plans">
      {plans.map((p) => (
        <div key={p.key} className="rounded-2xl border border-white/10 bg-[#0d0d14] p-5" data-testid={`plan-card-${p.key}`}>
          {edit?.key === p.key ? (
            <div className="space-y-2.5">
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} />
              <div><label className="text-xs text-zinc-500">{t("dash.maxProfiles")}</label>
                <input type="number" value={edit.max_profiles} onChange={(e) => setEdit({ ...edit, max_profiles: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-zinc-500">{t("dash.priceMonthly")}</label>
                <input type="number" step="0.01" value={edit.price_monthly} onChange={(e) => setEdit({ ...edit, price_monthly: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-zinc-500">{t("dash.priceYearly")}</label>
                <input type="number" step="0.01" value={edit.price_yearly} onChange={(e) => setEdit({ ...edit, price_yearly: e.target.value })} className={inputCls} /></div>
              <div className="flex gap-2 pt-1">
                <button onClick={save} data-testid={`plan-save-${p.key}`} className="flex-1 bg-brand-gradient text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEdit(null)} className="flex-1 glass text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-white">{p.name}</h3>
                <button onClick={() => setEdit(p)} data-testid={`plan-edit-${p.key}`} className="p-1.5 rounded-lg glass text-zinc-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
              </div>
              <p className="mt-3 font-display font-bold text-2xl text-white">{p.price_monthly.toFixed(2)}€<span className="text-sm text-zinc-500">/{t("dash.perMonthShort")}</span></p>
              <p className="text-sm text-zinc-400">{p.price_yearly}€/{t("dash.perYearShort")}</p>
              <p className="mt-2 text-sm text-cyan-400">{p.max_profiles} {t("dash.profilesMax")}</p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function CompanySub() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/company/overview").then(({ data }) => setData(data)); }, []);
  if (!data) return <Spinner />;
  const c = data.company;
  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d14] p-8 max-w-xl" data-testid="company-sub">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-brand-gradient flex items-center justify-center"><CreditCard className="w-5 h-5 text-white" /></span>
        <div>
          <p className="font-display font-bold text-xl text-white uppercase">{c.plan}</p>
          <StatusBadge status={c.subscription_status} />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-4 text-center">
        {[[t("dash.bought"), data.usage.purchased], [t("dash.used"), data.usage.active], [t("dash.availableCol"), data.usage.available]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-white/5 p-4"><p className="font-display font-bold text-2xl text-white">{v}</p><p className="text-xs text-zinc-500">{l}</p></div>
        ))}
      </div>
      {c.subscription_end && <p className="mt-4 text-sm text-zinc-400">{t("dash.deadline")} : {new Date(c.subscription_end).toLocaleDateString()}</p>}
      <p className="mt-4 text-sm text-zinc-500">{t("dash.contactToChange")}</p>
    </div>
  );
}

export default function Subscriptions() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user.role === "superadmin";
  return (
    <div>
      <h1 className="font-display font-bold text-3xl text-white">{isAdmin ? t("dash.subsTitle") : t("dash.mySubTitle")}</h1>
      <p className="mt-1 text-zinc-400">{isAdmin ? t("dash.subsSubAdmin") : t("dash.subsSubClient")}</p>
      {isAdmin ? <AdminPlans /> : <CompanySub />}
    </div>
  );
}
